import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, AppBar, Toolbar, Container
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LoginIcon from '@mui/icons-material/Login';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';

const highlights = [
  'Reduce reporting time by 90%',
  'Ensure 100% regulatory compliance',
  'Real-time emission tracking',
  'Enterprise-grade security',
  'Automated report generation',
  'API-first architecture',
];

const bulletPoints = [
  'Reduce reporting time by 90%',
  'Ensure 100% regulatory compliance',
  'Real-time emission tracking',
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#e3f2fd', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'transparent' }}>
        <Toolbar sx={{ px: { xs: 2, md: 6 }, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EnergySavingsLeafIcon sx={{ color: '#2e7d32', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e' }}>
              Carbon Measures
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            onClick={() => navigate('/login')}
            sx={{ color: '#1a237e', fontWeight: 600, mr: 1, textTransform: 'none', fontSize: '0.95rem' }}
          >
            Sign in
          </Button>
          <Button
            onClick={() => navigate('/login')}
            variant="outlined"
            sx={{
              borderColor: '#1a237e',
              color: '#1a237e',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              '&:hover': { borderColor: '#1a237e', bgcolor: 'rgba(26,35,126,0.04)' },
            }}
          >
            Sign up
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', py: { xs: 4, md: 0 } }}>
        <Container maxWidth="lg">
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            gap: { xs: 4, md: 8 },
          }}>
            {/* Left side: headline, description, bullets, CTAs */}
            <Box sx={{ flex: 1, maxWidth: { md: 560 } }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#1a237e', lineHeight: 1.2, mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
                Track carbon emissions with{' '}
                <Box component="span" sx={{ color: '#f9a825' }}>
                  complete confidence
                </Box>
              </Typography>

              <Typography variant="h6" sx={{ color: '#37474f', fontWeight: 400, mb: 3, lineHeight: 1.6, fontSize: { xs: '1rem', md: '1.15rem' } }}>
                The industry-leading platform for carbon accounting, e-liability management, and regulatory compliance. Trusted by energy companies worldwide.
              </Typography>

              <Box sx={{ mb: 4 }}>
                {bulletPoints.map((point) => (
                  <Box key={point} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 22 }} />
                    <Typography variant="body1" sx={{ color: '#263238', fontWeight: 500 }}>
                      {point}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => navigate('/login')}
                  sx={{
                    borderColor: '#1a237e',
                    color: '#1a237e',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.2,
                    borderRadius: 2,
                    fontSize: '0.95rem',
                    bgcolor: 'white',
                    '&:hover': { borderColor: '#1a237e', bgcolor: 'rgba(26,35,126,0.04)' },
                  }}
                >
                  Get Started Free
                </Button>
                <Button
                  variant="contained"
                  startIcon={<LoginIcon />}
                  onClick={() => navigate('/login')}
                  sx={{
                    bgcolor: '#1a237e',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.2,
                    borderRadius: 2,
                    fontSize: '0.95rem',
                    '&:hover': { bgcolor: '#0d1b5e' },
                  }}
                >
                  Sign In
                </Button>
              </Box>
            </Box>

            {/* Right side: Platform Highlights card */}
            <Card sx={{
              flex: { md: '0 0 380px' },
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
              bgcolor: 'white',
            }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <CheckCircleIcon sx={{ color: '#1565c0', fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e' }}>
                    Platform Highlights
                  </Typography>
                </Box>
                {highlights.map((item) => (
                  <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.8 }}>
                    <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                    <Typography variant="body1" sx={{ color: '#37474f', fontWeight: 500 }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
